import { useId, useState } from 'react';
import { ArrowLeft, Save } from 'lucide-react';
import { SetupCard } from '../ui';
import { safeId } from '../ui/labelledControl';
import { PaymentAccountSection } from './PaymentAccountSection';
import { AccessSettingsCard } from '../organization/AccessSettingsCard';
import { LinksSettingsCard } from '../organization/LinksSettingsCard';
import { AuditLogSettingsCard } from '../organization/AuditLogSettingsCard';
import {
  EquipmentSettingsCard,
  type EquipmentSettingsCardProps,
} from '../organization/EquipmentSettingsCard';

// ── THE PAGE'S WIDTH (the owner, 2026-10-21) ─────────────────────────
//
// `max-w-2xl` — 672px — was the monolith's width for a form of five fields,
// and it stayed when the page grew doors to other things (access, payments,
// equipment). Beside Hjem and Booking, which fill `max-w-7xl`, it read as a
// narrow strip down the middle. Now the page is `max-w-5xl` and on a wide
// screen it is TWO COLUMNS: the form on the left, and on the right the things
// that are the organization's but are not fields of this form — each with its
// own actions, none of them saved by this form's «Lagre». On a phone they
// stack, the doors first, as before.
const PAGE = 'max-w-5xl mx-auto px-4 md:px-6 py-10 slide-up';

export interface ClubSettingsViewProps {
  /** Current club info; each field is read defensively (falls back to empty/0). */
  clubInfo: any;
  /**
   * Persists the edited club info; awaited while the save spinner shows.
   *
   * `expectedName` is THE NAME THIS FORM OPENED ON — see `originalName` below.
   * It is a second argument rather than a field of `info` because it is not
   * something the person edited: it is what they were looking at when they
   * decided to change it.
   */
  onSave: (
    info: {
      name: string;
      address: string;
      phone: string;
      venue: string;
      courts: number;
      logoDataUrl: string;
    },
    expectedName: string,
  ) => Promise<{ acceptedName: string | null; stored: boolean } | void> | void;
  /** Navigates back to the previous screen. */
  onBack: () => void;
  /**
   * Has `clubInfo` been read yet? (#775 re-review.)
   *
   * REQUIRED, not optional. This form reads its props into its own inputs at
   * mount and never resynchronises — correct, because adopting a background
   * rename mid-edit is the stale-form window #774 closed. The consequence is
   * that it must NOT mount before the values exist: on a slow read it would
   * show blanks, `isDirty` would light the save button against the parent's
   * newly loaded values without anybody typing, and saving would write an
   * empty name over the real one.
   *
   * There is no readiness gate on the navigation that reaches this screen, so
   * the gate is here.
   */
  status: 'loading' | 'ready' | 'unavailable';
  /**
   * Opens the organization's «Tilganger» page (slice 3, decision 4A), or
   * null/absent for an account that does not manage access here — then no
   * entry is drawn at all. The shell decides from `/me`; this view only draws
   * the door it is handed, and the server refuses the route to everybody else.
   */
  onOpenAccess?: (() => void) | null;
  /**
   * Opens the organization's «Samarbeid» page (slice 4a), or null/absent for
   * an account that does not manage co-operation here — then nothing is drawn.
   */
  onOpenLinks?: (() => void) | null;
  /**
   * Opens the organization's «Logg» (#935), or null/absent for an account
   * that does not read the organization's audit trail — then nothing is drawn.
   */
  onOpenAuditLog?: (() => void) | null;
  /**
   * The organization's equipment registry — what the «Utstyr» card lists and
   * what its editor saves — or null/absent for an account that may not define
   * equipment here; then no card is drawn. The shell decides from `/me`
   * (`court.manage`, what the registry's writes ask for); this view only
   * draws what it is handed, and the server refuses the rest.
   */
  equipment?: EquipmentSettingsCardProps | null;
}

/** Club settings editor (name, logo, contact info, court count); extracted verbatim from the original monolith (legacy/App.jsx). */
export function ClubSettingsView({
  clubInfo,
  onSave,
  onBack,
  status,
  onOpenAccess = null,
  onOpenLinks = null,
  onOpenAuditLog = null,
  equipment = null,
}: ClubSettingsViewProps) {
  // ── NOT READY IS NOT AN EMPTY FORM ─────────────────────────────────
  //
  // Returned BEFORE the editor's state is created, so there is no half-built
  // form holding defaults: the inputs below are initialised from props on
  // their first render, and that render must be the one with real values in
  // it. A `disabled` form would still have captured the blanks.
  if (status !== 'ready') {
    return (
      <div className={PAGE}>
        <button
          onClick={onBack}
          className="text-xs font-mono text-ink-3 hover:text-ink-2 mb-2 flex items-center gap-1"
        >
          <ArrowLeft size={12} /> Tilbake
        </button>
        <h1 className="font-display text-3xl font-semibold mb-4">Klubbinnstillinger</h1>
        {status === 'loading' ? (
          <p className="text-sm text-ink-3">Laster klubbinformasjon…</p>
        ) : (
          <p className="text-sm text-danger">
            Vi fikk ikke lastet klubbinformasjonen. Last siden på nytt — redigering er slått av her
            for at ingenting skal bli overskrevet med blanke felt.
          </p>
        )}
      </div>
    );
  }
  return (
    <ClubSettingsForm
      clubInfo={clubInfo}
      onSave={onSave}
      onBack={onBack}
      onOpenAccess={onOpenAccess}
      onOpenLinks={onOpenLinks}
      onOpenAuditLog={onOpenAuditLog}
      equipment={equipment}
    />
  );
}

/** The editor itself. Mounted only once `clubInfo` is real — see above. */
function ClubSettingsForm({
  clubInfo,
  onSave,
  onBack,
  onOpenAccess = null,
  onOpenLinks = null,
  onOpenAuditLog = null,
  equipment = null,
}: Omit<ClubSettingsViewProps, 'status'>) {
  const [name, setName] = useState(clubInfo.name || '');
  // ── THE NAME THIS FORM HAS ACKNOWLEDGED (#775, twice over) ─────────
  //
  // The compare-and-swap baseline. NOT the live `clubInfo` prop: the hook
  // loads `club_info_v1` asynchronously and can replace it while this screen
  // is open, so reading the latest value at save time would send
  // `expectedName: B` for a person who only ever saw A — and the server would
  // accept it, replacing a name nobody agreed to replace. That is the
  // stale-form window #774 closed on the API side, reopened from the browser.
  //
  // AND IT IS NOT FROZEN AT MOUNT EITHER, which is what the first fix got
  // wrong. After a successful rename A→B the form stayed mounted with
  // `name = B` and a baseline of A, so the NEXT save — a phone number, say —
  // re-sent `expectedName: A` against a server holding B and answered 409. The
  // person had to reload to change anything after renaming, and the
  // partial-success recovery this PR introduces could not recover at all.
  //
  // So it advances on EXACTLY ONE EVENT: this form's own rename committing,
  // reported back as `acceptedName`. Not on a background prop change, not on
  // a refusal, and not on an outcome nobody knows.
  const [acknowledgedName, setAcknowledgedName] = useState(clubInfo.name || '');
  const [address, setAddress] = useState(clubInfo.address || '');
  const [phone, setPhone] = useState(clubInfo.phone || '');
  const [venue, setVenue] = useState(clubInfo.venue || '');
  const [courts, setCourts] = useState(clubInfo.courts || 0);
  const [logoDataUrl, setLogoDataUrl] = useState(clubInfo.logoDataUrl || '');
  const [logoError, setLogoError] = useState('');
  const [saving, setSaving] = useState(false);

  // Check whether anything has changed from the original
  const isDirty =
    name !== (clubInfo.name || '') ||
    address !== (clubInfo.address || '') ||
    phone !== (clubInfo.phone || '') ||
    venue !== (clubInfo.venue || '') ||
    courts !== (clubInfo.courts || 0) ||
    logoDataUrl !== (clubInfo.logoDataUrl || '');

  const handleLogoUpload = async (e) => {
    setLogoError('');
    const file = e.target.files?.[0];
    if (!file) return;

    // Check size — max 500kB raw file (base64 becomes roughly 33% larger)
    if (file.size > 500 * 1024) {
      setLogoError('Filen er for stor (maks 500 kB). Komprimér den eller velg et mindre bilde.');
      e.target.value = ''; // reset input
      return;
    }

    if (!file.type.startsWith('image/')) {
      setLogoError('Filen må være et bilde (PNG, JPG eller WebP).');
      e.target.value = '';
      return;
    }

    // Read as a data URL and resize to max 256x256 to reduce storage space
    try {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error('Kunne ikke lese fil'));
        reader.readAsDataURL(file);
      });

      // Resize via canvas — preserve aspect ratio, max 256px on the longest side
      const img = new Image();
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = () => reject(new Error('Kunne ikke laste bilde'));
        img.src = dataUrl;
      });

      const maxSize = 256;
      const ratio = Math.min(maxSize / img.width, maxSize / img.height, 1);
      const targetW = Math.round(img.width * ratio);
      const targetH = Math.round(img.height * ratio);

      const canvas = document.createElement('canvas');
      canvas.width = targetW;
      canvas.height = targetH;
      const ctx = canvas.getContext('2d');
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, targetW, targetH);

      // PNG preserves transparency (logos); JPEG would strip it
      const resizedDataUrl = canvas.toDataURL('image/png');
      setLogoDataUrl(resizedDataUrl);
    } catch (err) {
      console.error('[ClubSettings] Logo-feil:', err);
      setLogoError('Kunne ikke behandle bildet. Prøv et annet format.');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await onSave(
        {
          name: name.trim(),
          address: address.trim(),
          phone: phone.trim(),
          venue: venue.trim(),
          courts: parseInt(courts, 10) || 0,
          logoDataUrl,
        },
        acknowledgedName,
      );
      // ── THE BASELINE ADVANCES ONLY ON A COMMITTED RENAME ──────────
      //
      // `acceptedName` is non-null exactly when this save renamed the
      // organization and the server said so — including when the blob write
      // afterwards failed, which is the case the person needs to retry from.
      //
      // The INPUT is set to the same value, so a name the API normalised
      // differently from what was typed does not leave the field permanently
      // dirty against a baseline it can never match.
      if (result && result.acceptedName !== null) {
        setAcknowledgedName(result.acceptedName);
        setName(result.acceptedName);
      }
    } catch (e) {
      // ── CAUGHT, NOT JUST UNWOUND ──────────────────────────────────
      //
      // `finally` alone brings the button back and still lets the rejection
      // escape an async click handler — an unhandled promise rejection, which
      // the full test run reported as an error even though every assertion
      // passed. In a browser it reaches the console and any error overlay for
      // a case the person has already been told about.
      //
      // NO TOAST HERE. Saying what went wrong is `saveClubInfo`'s job and it
      // does it; a second message from this layer would either duplicate or
      // contradict it. This exists so the rejection stops somewhere.
      console.error('[ClubSettings] Lagring feilet:', e);
    } finally {
      // SO THE BUTTON ALWAYS COMES BACK. `setSaving(false)` sat after the
      // await, so a rejected save never reached it and the only way out of a
      // disabled button reading «Lagrer…» was a reload. The hook is also
      // written not to reject; this is the safety net, because a stuck button
      // is the one failure mode the person cannot work around.
      setSaving(false);
    }
  };

  // THE LABELS NAME THEIR FIELDS (#900). Each label used to sit beside its
  // input with nothing connecting them, so a screen reader reached five
  // unnamed inputs and clicking a label focused nothing. Wired by hand with
  // `Field`'s own id helper rather than through `<Field>`, because `Field`'s
  // label is semibold and its hint sits closer — a visible change to a screen
  // whose look this does not touch (as in #941).
  const uid = safeId('club-settings', useId());
  const ids = {
    name: `${uid}-name`,
    nameHint: `${uid}-name-hint`,
    venue: `${uid}-venue`,
    address: `${uid}-address`,
    phone: `${uid}-phone`,
    courts: `${uid}-courts`,
    courtsHint: `${uid}-courts-hint`,
  };

  return (
    <div className={PAGE}>
      <button
        onClick={onBack}
        className="text-xs font-mono text-ink-3 hover:text-ink-2 mb-2 flex items-center gap-1"
      >
        <ArrowLeft size={12} /> Tilbake
      </button>

      <div className="mb-6">
        <h1 className="font-display text-3xl font-semibold mb-1">Klubbinnstillinger</h1>
        <p className="text-sm text-ink-3">
          Informasjon om klubben din. Antall baner brukes som default i turneringsformater.
        </p>
      </div>

      <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,22rem)] lg:gap-6 lg:items-start">
        {/* THE DOORS — the organization's, but not fields of this form. First
            in the DOM so a phone shows them on top, as before; on a wide
            screen the grid puts them in the right-hand column. */}
        <aside className="space-y-4 mb-4 lg:mb-0 lg:col-start-2 lg:row-start-1">
          {/* TILGANGER (slice 3). Drawn only for an access manager — see the prop. */}
          {onOpenAccess && <AccessSettingsCard onOpen={onOpenAccess} />}
          {/* SAMARBEID (slice 4a). Drawn only for whoever manages co-operation here. */}
          {onOpenLinks && <LinksSettingsCard onOpen={onOpenLinks} />}
          {/* LOGG (#935). Drawn only for whoever reads the organization's trail. */}
          {onOpenAuditLog && <AuditLogSettingsCard onOpen={onOpenAuditLog} />}

          {/* UTSTYR (2026-10-21). Drawn only for somebody who may define it — see the prop. */}
          {equipment && <EquipmentSettingsCard {...equipment} />}

          {/* THE ORGANIZATION'S PAYMENT ACCOUNT (#946). Its own section with its
              own actions: nothing in it is saved by the form's button, which is
              why it lives among the doors and not under the fields. */}
          <PaymentAccountSection />
        </aside>

        <div className="space-y-4 lg:col-start-1 lg:row-start-1">
          {/* Club name + logo (top card) */}
          <SetupCard label="Klubbidentitet">
            <div className="flex items-start gap-4">
              {/* Logo preview + upload */}
              <div className="flex-shrink-0">
                <div className="w-24 h-24 rounded-xl overflow-hidden bg-bg-soft flex items-center justify-center border-2 border-dashed border-line">
                  {logoDataUrl ? (
                    <img src={logoDataUrl} alt="Klubblogo" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-ink-3 text-[10px] font-mono uppercase tracking-wider text-center px-2 leading-tight">
                      Ingen logo
                    </span>
                  )}
                </div>
                <div className="mt-2 flex flex-col gap-1">
                  <label className="btn-outline text-[11px] cursor-pointer text-center">
                    {logoDataUrl ? 'Bytt logo' : 'Last opp'}
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      onChange={handleLogoUpload}
                      className="hidden"
                    />
                  </label>
                  {logoDataUrl && (
                    <button
                      onClick={() => setLogoDataUrl('')}
                      className="text-[10px] text-rose hover:underline"
                    >
                      Fjern logo
                    </button>
                  )}
                </div>
              </div>

              {/* Name field */}
              <div className="flex-1 min-w-0">
                <label
                  htmlFor={ids.name}
                  className="font-mono text-[10px] uppercase tracking-wider text-ink-3 mb-1.5 block"
                >
                  Klubbens navn
                </label>
                <input
                  id={ids.name}
                  aria-describedby={ids.nameHint}
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="f.eks. Padel Oslo"
                  className="input w-full text-sm"
                />
                <p id={ids.nameHint} className="text-[11px] text-ink-3 mt-2">
                  Vises i header og brukes i turneringsdokumentasjon.
                </p>
              </div>
            </div>
            {logoError && (
              <div className="mt-2 px-3 py-2 rounded bg-rose-soft border border-rose text-[11px] text-rose">
                {logoError}
              </div>
            )}
          </SetupCard>

          {/* Contact info */}
          <SetupCard label="Kontaktinformasjon">
            <div className="space-y-3">
              <div>
                <label
                  htmlFor={ids.venue}
                  className="font-mono text-[10px] uppercase tracking-wider text-ink-3 mb-1.5 block"
                >
                  Treningssenter / hall
                </label>
                <input
                  id={ids.venue}
                  type="text"
                  value={venue}
                  onChange={(e) => setVenue(e.target.value)}
                  placeholder="f.eks. Padel Senter Oslo"
                  className="input w-full text-sm"
                />
              </div>
              <div>
                <label
                  htmlFor={ids.address}
                  className="font-mono text-[10px] uppercase tracking-wider text-ink-3 mb-1.5 block"
                >
                  Adresse
                </label>
                <input
                  id={ids.address}
                  type="text"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="f.eks. Padelveien 1, 0123 Oslo"
                  className="input w-full text-sm"
                />
              </div>
              <div>
                <label
                  htmlFor={ids.phone}
                  className="font-mono text-[10px] uppercase tracking-wider text-ink-3 mb-1.5 block"
                >
                  Telefonnummer
                </label>
                <input
                  id={ids.phone}
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="f.eks. +47 123 45 678"
                  className="input w-full text-sm"
                />
              </div>
            </div>
          </SetupCard>

          {/* Facility */}
          <SetupCard label="Anlegg">
            <div>
              <label
                htmlFor={ids.courts}
                className="font-mono text-[10px] uppercase tracking-wider text-ink-3 mb-1.5 block"
              >
                Antall padelbaner
              </label>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCourts(Math.max(0, parseInt(courts, 10) - 1 || 0))}
                  className="px-3 py-1.5 border border-line rounded text-sm hover:bg-bg-soft"
                >
                  −
                </button>
                <input
                  id={ids.courts}
                  aria-describedby={ids.courtsHint}
                  type="number"
                  value={courts}
                  onChange={(e) => setCourts(Math.max(0, parseInt(e.target.value, 10) || 0))}
                  min="0"
                  max="50"
                  className="input w-20 text-center text-sm font-mono"
                />
                <button
                  onClick={() => setCourts((parseInt(courts, 10) || 0) + 1)}
                  className="px-3 py-1.5 border border-line rounded text-sm hover:bg-bg-soft"
                >
                  +
                </button>
                <span className="text-xs text-ink-3 ml-2">
                  {courts > 0 ? `${courts * 4} spillere samtidig` : 'Ikke satt'}
                </span>
              </div>
              <p id={ids.courtsHint} className="text-[11px] text-ink-3 mt-2">
                Brukes som default antall baner i alle turneringsoppsett (kan overstyres i hver
                enkelt turnering).
              </p>
            </div>
          </SetupCard>

          {/* Save button */}
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onBack} className="btn-outline text-sm">
              Avbryt
            </button>
            <button
              onClick={handleSave}
              disabled={!isDirty || saving}
              className={`text-sm ${isDirty && !saving ? 'btn-lime' : 'btn-outline opacity-50 cursor-not-allowed'}`}
            >
              <Save size={12} /> {saving ? 'Lagrer…' : 'Lagre endringer'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
